/**
 * Multi address email handling.
 *
 * An email field may hold a primary address plus secondary ones, stored in a
 * single column as a comma separated list. Mirrors App\Helpers\EmailHelper
 * (twin-laravel), which is what EmailService already splits on before handing
 * each address to the mailer, so both ends agree on where one address ends and
 * the next begins.
 *
 * Commas and semicolons are those separators, which is exactly why a single
 * address may not contain one: a stored "a@b.com;c@d.com" would come back out
 * as two addresses no matter what the user meant by it. The input strips them
 * rather than storing a value that falls apart on the way back.
 */

/** Most addresses one field is allowed to hold. Drives the UI repeater. */
export const MAX_EMAILS = 3;

/** Separators that mean "another address follows". */
const SEPARATORS = /\s*[,;]\s*/;

/** The same characters, for stripping them out of a single address. */
const SEPARATOR_CHARS = /[,;]/g;

/**
 * Deliberately loose. Its job is to catch a half typed or obviously wrong
 * address, not to adjudicate RFC 5322 - the server's `email` rule is the
 * authority and rejecting here what the server would accept only strands the
 * user. Spaces and separators are excluded because both split the value.
 */
const ADDRESS = /^[^\s@,;]+@[^\s@,;.]+(?:\.[^\s@,;.]+)+$/;

/** Drops the characters that would split one address into two. */
export function stripEmailSeparators(value: string): string {
  return (value || '').replace(SEPARATOR_CHARS, '');
}

/** True when the character cannot appear inside a single address. */
export function isEmailSeparator(character: string): boolean {
  return character === ',' || character === ';';
}

/**
 * Split a stored value into its individual addresses.
 *
 * Parts are never dropped, so a legacy value holding more than MAX_EMAILS
 * still round trips in full. Pass max only when truncation is intended.
 */
export function emailParts(value: string, max?: number): string[] {
  if (!value || !value.trim()) {
    return [];
  }

  // Only blank segments are discarded. Anything else is handed back as it is,
  // so a legacy "a@b.com (work)" survives rather than being reshaped into
  // something the user never typed.
  let segments = value.trim().split(SEPARATORS)
    .map(segment => segment.trim())
    .filter(segment => segment !== '');

  if (max != null) {
    segments = segments.slice(0, max);
  }

  return segments;
}

/** Canonical form of a whole field value. */
export function formatEmail(value: string, max?: number): string {
  return emailParts(value, max).join(', ');
}

/**
 * Whether every address in the value is a usable one.
 *
 * Emptiness passes: whether the field is mandatory is `required`'s job.
 */
export function isValidEmail(value: string): boolean {
  if (!value || !value.trim()) {
    return true;
  }

  const parts = emailParts(value);
  if (!parts.length) {
    return false;
  }

  return parts.every(part => ADDRESS.test(part));
}
